import React, { useState } from 'react';
import { Users, Building2, DollarSign, Activity, MoreHorizontal, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
export function SuperAdminPage() {
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  };
  const metrics = [{
    label: 'Total Tenants',
    value: '48',
    change: '+4 this month',
    icon: Building2,
    color: 'bg-purple-100 text-purple-600'
  }, {
    label: 'Active Users',
    value: '12,450',
    change: '+12% growth',
    icon: Users,
    color: 'bg-blue-100 text-blue-600'
  }, {
    label: 'Monthly Revenue',
    value: '$284k',
    change: '+8.2% vs last mo',
    icon: DollarSign,
    color: 'bg-emerald-100 text-emerald-600'
  }, {
    label: 'System Uptime',
    value: '99.97%',
    change: 'All systems operational',
    icon: Activity,
    color: 'bg-amber-100 text-amber-600'
  }];
  const tenants = [{
    id: 1,
    name: 'Acme Corp',
    plan: 'Enterprise',
    users: 1240,
    spend: '$45,200',
    status: 'Active',
    created: 'Oct 2022'
  }, {
    id: 2,
    name: 'Globex Inc',
    plan: 'Business',
    users: 85,
    spend: '$8,400',
    status: 'Active',
    created: 'Jan 2023'
  }, {
    id: 3,
    name: 'Wayne Enterprises',
    plan: 'Enterprise',
    users: 3500,
    spend: '$120,000',
    status: 'Active',
    created: 'Mar 2022'
  }, {
    id: 4,
    name: 'Stark Industries',
    plan: 'Enterprise',
    users: 850,
    spend: '$32,500',
    status: 'Trial',
    created: 'Nov 2023'
  }, {
    id: 5,
    name: 'Umbrella Corp',
    plan: 'Business',
    users: 420,
    spend: '$15,800',
    status: 'Suspended',
    created: 'Jun 2023'
  }, {
    id: 6,
    name: 'Initech',
    plan: 'Starter',
    users: 45,
    spend: '$2,100',
    status: 'Active',
    created: 'Sep 2023'
  }];
  return <div className="space-y-6" data-id="element-2758">
      <div className="flex items-center justify-between" data-id="element-2759">
        <div data-id="element-2760">
          <h2 className="text-2xl font-bold text-slate-800" data-id="element-2761">
            Platform Overview
          </h2>
          <p className="text-slate-500" data-id="element-2762">Super Admin Dashboard</p>
        </div>
        <button onClick={() => showToast('Tenant creation initiated')} className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700" data-id="element-2763">
          + Add Tenant
        </button>
      </div>

      {/* Toast */}
      {toastMessage && <div className="fixed top-20 right-6 z-50 animate-in slide-in-from-top-2" data-id="element-2764">
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-4 shadow-lg flex items-center gap-3" data-id="element-2765">
            <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" data-id="element-2766" />
            <p className="font-medium text-sm" data-id="element-2767">{toastMessage}</p>
          </div>
        </div>}

      {/* Metrics */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2768">
        {metrics.map(metric => <div key={metric.label} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-2769">
            <div className="flex items-center justify-between" data-id="element-2770">
              <div className={`rounded-lg p-3 ${metric.color}`} data-id="element-2771">
                <metric.icon className="h-6 w-6" data-id="element-2772" />
              </div>
            </div>
            <div className="mt-4" data-id="element-2773">
              <h3 className="text-2xl font-bold text-slate-900" data-id="element-2774">
                {metric.value}
              </h3>
              <p className="text-sm text-slate-500" data-id="element-2775">{metric.label}</p>
              <p className="mt-1 text-xs font-medium text-emerald-600" data-id="element-2776">
                {metric.change}
              </p>
            </div>
          </div>)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2777">
        {/* Tenant Table */}
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-2778">
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4" data-id="element-2779">
            <h3 className="font-semibold text-slate-800" data-id="element-2780">Tenant Management</h3>
            <div className="flex gap-2" data-id="element-2781">
              <input type="text" placeholder="Search tenants..." className="text-sm border rounded-lg px-3 py-1.5" data-id="element-2782" />
            </div>
          </div>
          <div className="overflow-x-auto" data-id="element-2783">
            <table className="w-full text-left text-sm" data-id="element-2784">
              <thead className="bg-slate-50 text-slate-500" data-id="element-2785">
                <tr data-id="element-2786">
                  <th className="px-6 py-3 font-medium" data-id="element-2787">Organization</th>
                  <th className="px-6 py-3 font-medium" data-id="element-2788">Plan</th>
                  <th className="px-6 py-3 font-medium" data-id="element-2789">Users</th>
                  <th className="px-6 py-3 font-medium" data-id="element-2790">Monthly Spend</th>
                  <th className="px-6 py-3 font-medium" data-id="element-2791">Status</th>
                  <th className="px-6 py-3 font-medium" data-id="element-2792"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200" data-id="element-2793">
                {tenants.map(tenant => <tr key={tenant.id} className="hover:bg-slate-50" data-id="element-2794">
                    <td className="px-6 py-4 font-medium text-slate-900" data-id="element-2795">
                      {tenant.name}
                    </td>
                    <td className="px-6 py-4 text-slate-600" data-id="element-2796">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${tenant.plan === 'Enterprise' ? 'bg-purple-100 text-purple-700' : tenant.plan === 'Business' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`} data-id="element-2797">
                        {tenant.plan}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-600" data-id="element-2798">{tenant.users}</td>
                    <td className="px-6 py-4 font-medium text-slate-900" data-id="element-2799">
                      {tenant.spend}
                    </td>
                    <td className="px-6 py-4" data-id="element-2800">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${tenant.status === 'Active' ? 'bg-emerald-100 text-emerald-800' : tenant.status === 'Trial' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`} data-id="element-2801">
                        {tenant.status === 'Active' ? <CheckCircle className="h-3 w-3" data-id="element-2802" /> : tenant.status === 'Trial' ? <AlertCircle className="h-3 w-3" data-id="element-2803" /> : <XCircle className="h-3 w-3" data-id="element-2804" />}
                        {tenant.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2805">
                      <button className="text-slate-400 hover:text-slate-600" data-id="element-2806">
                        <MoreHorizontal className="h-5 w-5" data-id="element-2807" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {/* Activity Log */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-id="element-2808">
          <h3 className="font-semibold text-slate-800 mb-4" data-id="element-2809">Recent Activity</h3>
          <div className="space-y-6" data-id="element-2810">
            {[{
            text: 'New tenant registered: Globex Inc',
            time: '2 hours ago',
            type: 'new'
          }, {
            text: 'Acme Corp upgraded to Enterprise plan',
            time: '5 hours ago',
            type: 'upgrade'
          }, {
            text: 'System maintenance scheduled',
            time: '1 day ago',
            type: 'system'
          }, {
            text: 'Umbrella Corp payment failed',
            time: '1 day ago',
            type: 'alert'
          }, {
            text: 'New feature deployed: AI Assistant v2',
            time: '2 days ago',
            type: 'system'
          }].map((item, i) => <div key={i} className="flex gap-3" data-id="element-2811">
                <div className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${item.type === 'new' ? 'bg-emerald-500' : item.type === 'upgrade' ? 'bg-blue-500' : item.type === 'alert' ? 'bg-red-500' : 'bg-slate-400'}`} data-id="element-2812" />
                <div data-id="element-2813">
                  <p className="text-sm text-slate-800" data-id="element-2814">{item.text}</p>
                  <p className="text-xs text-slate-500" data-id="element-2815">{item.time}</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}